import { useEffect, useState } from "react";
import { Download, FileText, Loader2, ArrowLeft, Smartphone, ShieldAlert, Sparkles, X } from "lucide-react";
import { api, formatApiErrorDetail, agreementDownloadUrl, formatBytes } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

export default function DownloadPage({ type }) {
  const [contactEmail, setContactEmail] = useState("");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);

  const fileShortId = window.location.pathname.split("/").pop();
  const url =
    type === "agreement"
      ? "/api/agreement/download"
      : type === "setup"
      ? "/api/download"
      : `/api/files/${fileShortId}`;
  const isExecutable = /\.(exe|msi|msp|bat|cmd|scr|ps1|com|jar|apk|msix|appx)$/i.test(info?.name || "");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/settings")
      .then((r) => {
        if (!cancelled) setContactEmail(r.data.email || "");
      })
      .catch(() => {});
    if (type === "agreement") {
      api
        .get("/settings")
        .then((r) => {
          if (!cancelled) setInfo({ name: r.data.agreement.filename, size: r.data.agreement.size });
        })
        .catch(() => {
          if (!cancelled) setInfo({ name: "Fleetguard-Client-Agreement.pdf", size: 0 });
        });
    } else if (type === "setup") {
      api
        .get("/download/info")
        .then((r) => {
          if (!cancelled) setInfo({ name: r.data.name, size: r.data.size });
        })
        .catch((err) => {
          if (!cancelled)
            setError(
              err.response?.status === 404
                ? "No setup file is available yet — please check back soon."
                : formatApiErrorDetail(err.response?.data?.detail),
            );
        });
    } else if (!fileShortId || fileShortId.length < 4) {
      setError("This file link is invalid.");
    } else {
      api
        .get(`/files/${fileShortId}/info`)
        .then((r) => {
          if (!cancelled) setInfo({ name: r.data.name, size: r.data.size });
        })
        .catch((err) => {
          if (!cancelled)
            setError(
              err.response?.status === 404
                ? "This file link is invalid or the file was removed."
                : formatApiErrorDetail(err.response?.data?.detail),
            );
        });
    }
    return () => {
      cancelled = true;
    };
  }, [type, fileShortId]);

  useEffect(() => {
    if (!info) return;
    const t = setTimeout(() => {
      window.location.assign(url);
      setStarted(true);
    }, 900);
    return () => clearTimeout(t);
  }, [info, url]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B1120] px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="text-center" data-testid="download-page">
          <a href="/" className="inline-flex justify-center" data-testid="download-logo-link">
            <LogoMark className="h-12 w-12" />
          </a>

          {error ? (
            <>
              <h1 className="mt-6 font-display text-2xl font-bold tracking-tight text-white">{error}</h1>
              <a
                href="/"
                data-testid="download-back-link"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-400"
              >
                <ArrowLeft className="h-4 w-4" /> Back to Fleetguard Insurance
              </a>
            </>
          ) : !info ? (
            <Loader2 className="mx-auto mt-8 h-6 w-6 animate-spin text-blue-400" />
          ) : (
            <>
              <div
                className="mt-6 inline-flex max-w-full items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2"
                data-testid="download-file-name"
              >
                <FileText className="h-4 w-4 shrink-0 text-blue-400" />
                <span className="min-w-0 truncate font-mono text-xs text-slate-300">
                  {info.name}
                  {info.size ? ` · ${formatBytes(info.size)}` : ""}
                </span>
              </div>

              <div className="mt-5">
                <a
                  href={url}
                  data-testid="download-now-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-8 py-3 text-base font-semibold text-white shadow-lg shadow-blue-600/30 transition-all hover:-translate-y-0.5 hover:bg-brand-bluedeep"
                >
                  <Download className="h-5 w-5" /> Download again
                </a>
              </div>

              <div className="mt-10 rounded-3xl border border-white/5 bg-[#111A2E] p-6 text-left shadow-2xl shadow-black/40 sm:p-8">
                <div className="flex gap-4" data-testid="download-step-1">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-blue-400/20 bg-blue-500/10">
                    <Download className="h-5 w-5 text-blue-400" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-400">Step 1</p>
                    <h2 className="mt-1 font-display text-lg font-bold text-white">
                      {isExecutable ? "Download and run the setup file" : "Save the file"}
                    </h2>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-400">
                      Save the file when your browser asks, then open it from your Downloads folder
                      {isExecutable ? ` (e.g. ${info.name}) to start the installation.` : " to view or share it."}
                    </p>
                  </div>
                </div>

                {isExecutable ? (
                  <div className="mt-9 flex gap-4" data-testid="smartscreen-instructions">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-blue-400/20 bg-blue-500/10">
                      <ShieldAlert className="h-5 w-5 text-blue-400" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-400">Step 2</p>
                      <h2 className="mt-1 font-display text-lg font-bold text-white">The Windows SmartScreen alert</h2>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-400">
                        On first run, Windows may show a blue alert that says{" "}
                        <span className="font-semibold text-slate-200">"Unknown publisher"</span>. This is standard for
                        newly published software that has not yet built up a Microsoft reputation — it does{" "}
                        <span className="font-semibold text-slate-200">not</span> mean the file is harmful.
                      </p>

                      <div
                        className="mt-4 overflow-hidden rounded-xl shadow-2xl shadow-black/50"
                        data-testid="smartscreen-mockup"
                      >
                        <div className="bg-[#1A66CC] px-5 py-4">
                          <div className="flex items-start justify-between gap-4">
                            <p className="text-lg text-white">Windows protected your PC</p>
                            <X className="mt-1 h-4 w-4 shrink-0 text-white/80" />
                          </div>
                          <p className="mt-3 text-sm text-white/90">
                            Publisher: <span className="font-bold text-white">Unknown publisher</span>
                          </p>
                          <span className="mt-3 inline-block rounded border border-white/80 px-3 py-1 text-sm text-white">
                            More info
                          </span>
                        </div>
                        <div className="flex justify-end bg-[#125BAF] px-5 py-3">
                          <span className="rounded border border-white/80 px-4 py-1 text-sm text-white">Don't run</span>
                        </div>
                      </div>

                      <p className="mt-4 flex items-start gap-2 text-[13px] leading-relaxed text-slate-400">
                        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                        <span>
                          In the real alert: press <span className="font-semibold text-slate-200">More info</span>, then
                          press <span className="font-semibold text-slate-200">Run anyway</span>
                        </span>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-9 flex gap-4" data-testid="regular-file-tips">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-blue-400/20 bg-blue-500/10">
                      <Smartphone className="h-5 w-5 text-blue-400" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-400">Step 2</p>
                      <h2 className="mt-1 font-display text-lg font-bold text-white">Open it anywhere</h2>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-400">
                        On iPhone or iPad: if the file opens in a viewer, tap Share → Save to Files.
                      </p>
                    </div>
                  </div>
                )}

                {started && (
                  <p className="mt-8 text-center text-xs text-slate-500" data-testid="download-fallback-note">
                    Didn't start? Use the "Download again" button above.
                  </p>
                )}

                <div className="mt-8 border-t border-white/10 pt-5">
                  <p className="text-center text-sm text-slate-500">
                    Questions? Email{" "}
                    <a
                      href={`mailto:${contactEmail || "sales@leetguard-ins.com"}`}
                      className="font-semibold text-slate-300 transition-colors hover:text-white"
                      data-testid="download-contact-link"
                    >
                      our operations desk
                    </a>{" "}
                    — we'll walk you through it in minutes.
                  </p>
                </div>
              </div>

              <a
                href="/"
                data-testid="download-back-link"
                className="mt-8 inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-blue-400"
              >
                <ArrowLeft className="h-4 w-4" /> Back to Fleetguard Insurance
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
